<template>
  <div class="patients-form-wrapper">
    <form
      @submit.prevent="$emit('submit')"
      :id="formId"
      class="patient-form-container"
    >
      <div class="form-fields">
          <div class="form-group">
            <label>سریال پرونده <span class="required">*</span></label>
            <input type="text" v-model="formData.fParno" required />
          </div>
          <div class="form-group">
            <label>نام <span class="required">*</span></label>
            <input type="text" v-model="formData.fTname" required />
          </div>
          <div class="form-group">
            <label>نام خانوادگی <span class="required">*</span></label>
            <input type="text" v-model="formData.fFami" required />
          </div>
          <div class="form-group">
            <label>کد ملی</label>
            <input type="text" v-model="formData.pmeli" />
          </div>
          <div class="form-group">
            <label>تاریخ مراجعه اولیه</label>
            <div class="p-date">
              <DatePicker
                v-model="formData.fFirstdt"
                format="jYYYY/jMM/jDD"
                display-format="jYYYY/jMM/jDD"
                placeholder="تاریخ را وارد یا انتخاب کنید"
                :editable="true"
                auto-submit
              />
            </div>
          </div>
          <div class="form-group">
            <label>جنسیت</label>
            <select v-model="formData.fSex">
              <option value="">انتخاب کنید</option>
              <option value="1">زن</option>
              <option value="2">مرد</option>
            </select>
          </div>
          <div class="form-group">
            <label>نوع بیمه <span class="required">*</span></label>
            <select v-model="formData.fBimeh" required>
              <option value="">انتخاب کنید</option>
              <option value="1">آزاد</option>
              <option value="2">تامین اجتماعی</option>
              <option value="3">نیروهای مسلح</option>
            </select>
          </div>
          <div class="form-group">
            <label>شماره بیمه</label>
            <input type="text" v-model="formData.fBimehno" />
          </div>
        </div>
      <div class="bottom-form-fields">
        <div class="age-birth-wrapper span-4">
          <div class="form-group birth-date-group">
            <label>تاریخ تولد</label>
            <div class="p-date">
              <DatePicker
                v-model="formData.fBrdt"
                format="jYYYY/jMM/jDD"
                display-format="jYYYY/jMM/jDD"
                placeholder="1331/01/01"
                :editable="true"
                auto-submit
              />
            </div>
          </div>
          <div class="form-group age-group">
            <label>سن</label>
            <input
              type="number"
              :value="calculatedAge"
              readonly
              placeholder="-"
            />
          </div>
        </div>
        <div class="form-group">
          <label>تلفن همراه</label
          ><input type="tel" v-model="formData.ptele" />
        </div>
        <div class="form-group">
          <label>تلفن ثابت</label
          ><input type="tel" v-model="formData.fTdesc2" />
        </div>
        <div class="form-group">
          <label>نام پدر</label
          ><input type="text" v-model="formData.fFather" />
        </div>
        <div class="form-group">
          <label>نام همسر</label
          ><input type="text" v-model="formData.pdesc2" />
        </div>
        <div class="form-group">
          <label>شغل</label><input type="text" v-model="formData.fJob" />
        </div>
        <div class="form-group">
          <label>معرف</label><input type="text" v-model="formData.fMoaref" />
        </div>
        <div class="form-group">
          <label>ش دستی پرونده</label
          ><input type="text" v-model="formData.fParold" />
        </div>
        <div class="form-group span-4">
          <label>آدرس</label
          ><textarea v-model="formData.paddr" rows="2"></textarea>
        </div>
        <div class="form-group span-4">
          <label>توضیحات</label
          ><textarea v-model="formData.Pdesc1" rows="3"></textarea>
        </div>
      </div>
    </form>
  </div>
</template>

<script setup>
import { computed, onMounted } from "vue";
import DatePicker from "vue3-persian-datetime-picker";
import moment from "jalali-moment";
import { storeToRefs } from "pinia";
import { usePatientsStore } from "@/stores/clinic/patients.js";

// --- Store Setup ---
const patientsStore = usePatientsStore();
const { fileSerialCounter } = storeToRefs(patientsStore);

// eslint-disable-next-line no-undef
const props = defineProps({
  modelValue: { type: Object, required: true },
  formId: { type: String, required: true },
});

// eslint-disable-next-line no-undef
const emit = defineEmits(["update:modelValue", "submit"]);

// v-model setup
const formData = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

// محاسبه سن
const calculatedAge = computed(() => {
  if (formData.value.birthDate) {
    const birthDateMoment = moment(formData.value.birthDate, "jYYYY/jMM/jDD");
    if (birthDateMoment.isValid()) {
      return moment().diff(birthDateMoment, "years");
    }
  }
  return "";
});

// مقداردهی اولیه تاریخ‌ها و سریال پرونده
onMounted(() => {
  if (!formData.value.fileSerial) {
    formData.value.fileSerial = fileSerialCounter.value;
  }

  if (!formData.value.birthDate) {
    formData.value.birthDate = moment().format("jYYYY/jMM/jDD");
  }

  if (!formData.value.initialVisitDate) {
    formData.value.initialVisitDate = moment().format("jYYYY/jMM/jDD");
  }
});
</script>

<style scoped>
/* The main form container is now a flexbox column */
.patient-form-container {
  display: flex;
  flex-direction: column;
  gap: 20px; /* Gap between form sections */
}

.age-birth-wrapper {
  display: flex;
  gap: 10px; /* Adjust the space between the two fields */
  align-items: flex-start; /* Aligns items to the top */
}

.birth-date-group {
  flex: 3; /* Takes up 3 parts of the space */
  width: 80% !important;
}

.age-group {
  flex: 1; /* Takes up 1 part of the space */
  width: 20% !important;
}

.age-group input {
  text-align: center;
}

.form-fields,
.bottom-form-fields {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.span-4 {
  grid-column: span 4;
}

.form-group label {
  margin-bottom: 5px;
  color: var(--tex-color-thirdly);
  text-align: justify;
}

.required {
  color: #ff0f0f;
  margin-right: 4px;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 5px;
  border: 1px solid var(--text-color-secondary);
  border-radius: 10px;
  text-align: start;
  width: 100%;
  background-color: var(--background-color-secondary);
  color: var(--text-color-primary);
}

.form-group input,
.form-group select {
  height: 34px;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--text-color-primary);
}

.form-actions {
  display: flex;
  justify-content: flex-start;
}

.p-date {
  border: 1px solid var(--text-color-secondary);
  border-radius: 10px;
  text-align: start;
  overflow: hidden;
  background-color: var(--background-color);
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.p-date :deep(.vpd-container) {
  border: 1px solid rgba(var(--text-color-secondary-rgb), 0.12);
  border-radius: 0.75rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  background-color: var(--background-color);
}

.p-date :deep(.vpd-input-group label) {
  background-color: var(--background-color-button) !important;
}

.p-date :deep(.vpd-input-group input) {
  border: none !important;
  line-height: 32px !important;
  background-color: var(--background-color) !important;
  color: var(--text-color-primary) !important;
}

.p-date :deep(.vpd-input-group input::placeholder) {
  color: var(--text-color-secondary) !important;
  opacity: 1;
}

.p-date :deep(.vpd-input-group input:focus) {
  border: 1px solid transparent !important;
}

.p-date :deep(.vpd-icon-btn),
.p-date :deep(button.vpd-icon-btn),
.p-date :deep(.vpd-actions button) {
  background-color: var(--background-color-button) !important;
  color: var(--background-color) !important;
  transition: background-color 0.3s ease !important;
}

.p-date :deep(.vpd-icon-btn svg),
.p-date :deep(button.vpd-icon-btn svg),
.p-date :deep(.vpd-actions button svg),
.p-date :deep(.vpd-icon-btn svg path),
.p-date :deep(button.vpd-icon-btn svg path),
.p-date :deep(.vpd-actions button svg path) {
  fill: var(--background-color) !important;
  color: var(--background-color) !important;
  stroke: var(--background-color) !important;
}

.p-date :deep(.vpd-icon-btn:hover),
.p-date :deep(button.vpd-icon-btn:hover),
.p-date :deep(.vpd-actions button:hover) {
  background-color: var(--background-color-button-hover) !important;
}

.submit-button {
  padding: 8px 20px;
  background-color: var(--background-color-button);
  color: var(--text-color-forthly);
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
}

.submit-button:hover {
  background-color: var(--background-color-button-hover);
}
@media (max-width: 768px) {
  .form-fields,
  .bottom-form-fields {
    grid-template-columns: 1fr;
  }
  .form-group.span-4 {
    grid-column: span 1;
  }
}
</style>